import { parseRange } from '#shared/types';
import { type TrendPoint, describeSeries } from './result-trend.logic';

/**
 * One analyte over a patient's visits.
 *
 * **A chart is justified here; a chart library is not.** One series, at most a
 * dozen points, no legend and no tooltips — and the house already draws this
 * shape in `Sparkline.tsx`. Adding a dependency for it would be adding a
 * dependency for a `<polyline>`.
 *
 * Three decisions that differ from `Sparkline`, and each has a reason:
 *
 *   * **No `preserveAspectRatio="none"`.** Sparkline stretches because it is a
 *     bare line. This has circular points and a reference band, and non-uniform
 *     scaling turns circles into ellipses.
 *   * **X spacing is by visit index, not by elapsed time.** Visits are
 *     irregular — three days, then eight months — and a time axis clumps four
 *     recent visits into one pixel column.
 *   * **Oldest on the left**, which is the opposite of the table beside it.
 *     Both orders are right for their own job, and the `<figcaption>` says so
 *     rather than papering over the mismatch.
 */

const WIDTH = 320;
const HEIGHT = 96;
const PAD = 10;

export function TrendChart({
  name,
  unit,
  range,
  points,
}: {
  name: string;
  unit: string;
  range: string;
  points: readonly TrendPoint[];
}) {
  const description = describeSeries({ name, unit, points, range });

  if (points.length < 2) {
    return (
      <figure className="c-trend">
        <p className="c-trend__empty">
          {points.length === 0
            ? 'Nothing numeric to chart.'
            : 'One visit — nothing to compare it with yet.'}
        </p>
      </figure>
    );
  }

  const bounds = parseRange(range);
  const values = points.map((point) => point.value);

  // The reference band is part of the scale, so a value inside it is visibly
  // inside it. Without that, a band drawn outside the plotted range would be
  // clipped and read as "no band".
  const candidates = [
    ...values,
    ...(bounds?.min !== undefined ? [bounds.min] : []),
    ...(bounds?.max !== undefined ? [bounds.max] : []),
  ];
  const low = Math.min(...candidates);
  const high = Math.max(...candidates);
  // A flat series would divide by zero and collapse to the baseline.
  const span = high - low || Math.abs(high) || 1;

  const x = (index: number) => PAD + (index * (WIDTH - 2 * PAD)) / (points.length - 1);
  const y = (value: number) => HEIGHT - PAD - ((value - low) / span) * (HEIGHT - 2 * PAD);

  const line = points.map((point, index) => `${x(index)},${y(point.value)}`).join(' ');

  // Unparseable range → **no band**, silently. The same rule `flagFor` follows:
  // a range this cannot read is not a range to draw, and inventing bounds would
  // put a clinical boundary on a chart that nobody set.
  const band =
    bounds && (bounds.min !== undefined || bounds.max !== undefined)
      ? {
          top: y(bounds.max ?? high),
          bottom: y(bounds.min ?? low),
        }
      : null;

  return (
    <figure className="c-trend">
      <svg
        className="c-trend__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={description}
      >
        {band && (
          <rect
            className="c-trend__band"
            x={0}
            y={Math.min(band.top, band.bottom)}
            width={WIDTH}
            height={Math.max(1, Math.abs(band.bottom - band.top))}
          />
        )}

        <polyline className="c-trend__line" points={line} fill="none" />

        {points.map((point, index) => (
          <circle
            key={point.report_id}
            className={point.flag ? 'c-trend__point c-trend__point--flagged' : 'c-trend__point'}
            cx={x(index)}
            cy={y(point.value)}
            r={3}
          />
        ))}
      </svg>

      <figcaption className="c-trend__caption">
        Oldest visit on the left. The table above reads newest first.
      </figcaption>
    </figure>
  );
}
